/**
 * Profile Service
 * Centralized data layer for user profile operations
 * Works with Supabase backend and RLS policies
 */

import { supabaseClient } from '@/shared/services/client';
import { UserProfile } from '@/shared/types';

export interface ProfileCreateInput {
  id: string;
  email: string;
  name?: string;
  full_name?: string;
  avatar_url?: string;
  role?: string;
  country?: string;
  currency?: string;
}

export interface ProfileUpdateInput {
  name?: string;
  full_name?: string;
  email?: string;
  phone?: string;
  avatar_url?: string;
  business_name?: string;
  business_address?: string;
  business_email?: string;
  business_phone?: string;
  logo_url?: string;
  brand_color?: string;
  template?: string;
  currency?: string;
  country?: string;
  has_completed_onboarding?: boolean;
  notification_preferences?: Record<string, any>;
}

/**
 * Profile Data Layer Service
 * Handles all profile CRUD operations with Supabase
 */
class ProfileService {
  private client = supabaseClient.getClient();

  /**
   * Get a profile by ID
   */
  async getProfile(profileId: string): Promise<UserProfile | null> {
    try {
      const { data, error } = await this.client
        .from('profiles')
        .select('*')
        .eq('id', profileId)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        throw error;
      }

      if (!data) {
        return null;
      }

      return this.mapToUserProfile(data);
    } catch (error) {
      console.error('Failed to get profile:', error);
      throw error;
    }
  }

  /**
   * Get a profile by email
   */
  async getProfileByEmail(email: string): Promise<UserProfile | null> {
    try {
      const { data, error } = await this.client
        .from('profiles')
        .select('*')
        .eq('email', email)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned
          return null;
        }
        console.error('Error fetching profile by email:', error);
        throw error;
      }

      if (!data) {
        return null;
      }

      return this.mapToUserProfile(data);
    } catch (error) {
      console.error('Failed to get profile by email:', error);
      throw error;
    }
  }

  /**
   * Create a new profile
   */
  async createProfile(input: ProfileCreateInput): Promise<UserProfile> {
    try {
      const now = new Date().toISOString();

      const profileData = {
        id: input.id,
        email: input.email,
        name: input.name || input.email?.split('@')[0] || 'Unknown',
        full_name: input.full_name || input.name,
        avatar_url: input.avatar_url,
        role: input.role || 'freelancer',
        country: input.country || 'US',
        currency: input.currency || 'USD',
        created_at: now,
        updated_at: now,
        joined_at: now,
        has_completed_onboarding: false,
        notification_preferences: {},
        subscription_plan_id: 'free',
        subscription_status: 'active',
        billing_period: 'monthly',
        brand_color: '#3b82f6',
        template: 'default'
      };

      const { data, error } = await this.client
        .from('profiles')
        .insert(profileData)
        .select()
        .single();

      if (error) {
        console.error('Error creating profile:', error);
        throw error;
      }

      return this.mapToUserProfile(data);
    } catch (error) {
      console.error('Failed to create profile:', error);
      throw error;
    }
  }

  /**
   * Update a profile
   */
  async updateProfile(profileId: string, input: ProfileUpdateInput): Promise<UserProfile> {
    try {
      const updateData: Record<string, unknown> = {
        updated_at: new Date().toISOString()
      };

      // Map input fields to database columns
      if (input.name !== undefined) updateData.name = input.name;
      if (input.full_name !== undefined) updateData.full_name = input.full_name;
      if (input.email !== undefined) updateData.email = input.email;
      if (input.phone !== undefined) updateData.phone = input.phone;
      if (input.avatar_url !== undefined) updateData.avatar_url = input.avatar_url;
      if (input.business_name !== undefined) updateData.business_name = input.business_name;
      if (input.business_address !== undefined) updateData.business_address = input.business_address;
      if (input.business_email !== undefined) updateData.business_email = input.business_email;
      if (input.business_phone !== undefined) updateData.business_phone = input.business_phone;
      if (input.logo_url !== undefined) updateData.logo_url = input.logo_url;
      if (input.brand_color !== undefined) updateData.brand_color = input.brand_color;
      if (input.template !== undefined) updateData.template = input.template;
      if (input.currency !== undefined) updateData.currency = input.currency;
      if (input.country !== undefined) updateData.country = input.country;
      if (input.has_completed_onboarding !== undefined) updateData.has_completed_onboarding = input.has_completed_onboarding;
      if (input.notification_preferences !== undefined) updateData.notification_preferences = input.notification_preferences;

      const { data, error } = await this.client
        .from('profiles')
        .update(updateData)
        .eq('id', profileId)
        .select()
        .single();

      if (error) {
        console.error('Error updating profile:', error);
        throw error;
      }

      return this.mapToUserProfile(data);
    } catch (error) {
      console.error('Failed to update profile:', error);
      throw error;
    }
  }

  /**
   * Get or create profile by email
   * Useful for Firebase auth integration
   */
  async getOrCreateProfileByEmail(
    email: string,
    firebaseUid?: string
  ): Promise<UserProfile> {
    try {
      // First, try to get existing profile
      const existingProfile = await this.getProfileByEmail(email);

      if (existingProfile) {
        // If Firebase UID provided, ensure mapping exists
        if (firebaseUid) {
          await this.ensureProfileMapping(firebaseUid, existingProfile.id);
        }
        return existingProfile;
      }

      // Create new profile
      const newProfileId = crypto.randomUUID();
      const nameFromEmail = email.split('@')[0] || 'Unknown';
      const newProfile = await this.createProfile({
        id: newProfileId,
        email: email,
        name: nameFromEmail
      });

      // If Firebase UID provided, create mapping
      if (firebaseUid) {
        await this.ensureProfileMapping(firebaseUid, newProfileId);
      }

      return newProfile;
    } catch (error) {
      console.error('Failed to get or create profile:', error);
      throw error;
    }
  }

  /**
   * Ensure profile mapping exists for Firebase UID
   */
  private async ensureProfileMapping(
    firebaseUid: string,
    supabaseId: string
  ): Promise<void> {
    try {
      // Check if mapping exists
      const { data: existingMapping } = await this.client
        .from('profile_mappings')
        .select('id')
        .eq('firebase_uid', firebaseUid)
        .single();

      if (existingMapping) {
        return; // Mapping already exists
      }

      // Create mapping
      const { error } = await this.client
        .from('profile_mappings')
        .insert({
          firebase_uid: firebaseUid,
          supabase_id: supabaseId,
          created_at: new Date().toISOString()
        });

      if (error && error.code !== '23505') {
        // Ignore duplicate key errors, log others
        console.warn('Warning: Could not create profile mapping:', error);
      }
    } catch (error) {
      console.warn('Warning: Error ensuring profile mapping:', error);
    }
  }

  /**
   * Get profile ID from Firebase UID
   */
  async getProfileIdByFirebaseUid(firebaseUid: string): Promise<string | null> {
    try {
      const { data, error } = await this.client
        .from('profile_mappings')
        .select('supabase_id')
        .eq('firebase_uid', firebaseUid)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }

      return data?.supabase_id || null;
    } catch (error) {
      console.error('Failed to get profile ID by Firebase UID:', error);
      return null;
    }
  }

  /**
   * Map database record to UserProfile type
   */
  private mapToUserProfile(data: any): UserProfile {
    return {
      id: data.id,
      userId: data.id,
      name: data.name || data.full_name || data.email?.split('@')[0] || 'Unknown',
      email: data.email || '',
      role: data.role || 'freelancer',
      avatarUrl: data.avatar_url,
      businessName: data.business_name,
      businessAddress: data.business_address,
      businessEmail: data.business_email || data.email,
      businessPhone: data.business_phone,
      logoUrl: data.logo_url,
      brandColor: data.brand_color || '#3b82f6',
      template: data.template || 'default',
      subscription: {
        planId: data.subscription_plan_id || 'free',
        status: data.subscription_status || 'active',
        nextBillingDate: data.next_billing_date,
        billingPeriod: data.billing_period || 'monthly'
      },
      currency: data.currency || 'USD',
      country: data.country || 'US',
      savedServices: data.saved_services || [],
      paymentMethods: data.payment_methods || [],
      joinedAt: data.joined_at || data.created_at,
      hasCompletedOnboarding: data.has_completed_onboarding ?? false,
      notificationPreferences: data.notification_preferences || {
        email: { jobUpdates: true, paymentReminders: true, clientOnboarding: true },
        whatsapp: { jobUpdates: false, paymentReminders: true, clientOnboarding: false },
        push: { jobUpdates: true, paymentReminders: true, clientOnboarding: true },
        sms: { jobUpdates: false, paymentReminders: false, clientOnboarding: false }
      }
    };
  }
}

// Export singleton instance
export const profileService = new ProfileService();
export default profileService;
